// store/StorePage.jsx
// Route: /:storeSlug (single segment — placed BEFORE /:storeSlug/:productSlug in App.jsx)
// Fetches store profile from /api/marketplace/store/:storeSlug
// Assembles: MarketplaceHeader + StoreHeader + StoreProductGrid + MarketplaceFooter

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, AlertCircle } from "lucide-react";
import { MarketplaceHeader } from "../shared/MarketplaceHeader";
import { MarketplaceFooter } from "../shared/MarketplaceFooter";
import { WhatsAppFloat }     from "../shared/WhatsAppFloat";
import { StoreHeader }       from "./StoreHeader";
import { StoreProductGrid }  from "./StoreProductGrid";

const API_URL = process.env.REACT_APP_API_URL;

export default function StorePage() {
  const { storeSlug } = useParams();
  const navigate = useNavigate();

  const [store,    setStore]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!storeSlug) return;
    setLoading(true);
    setNotFound(false);
    setStore(null);

    fetch(`${API_URL}/api/marketplace/store/${storeSlug}`)
      .then(r => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then(data => { if (data) setStore(data); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [storeSlug]);

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <MarketplaceHeader />
      <div>
        {/* Banner skeleton */}
        <div className="h-40 sm:h-52 bg-slate-200 animate-pulse" />
        <div className="px-4 relative">
          {/* Profile circle skeleton */}
          <div
            className="absolute size-24 sm:size-28 rounded-full bg-slate-300 animate-pulse border-4 border-white"
            style={{ top: -48 }}
          />
          <div className="pt-4 pl-32 sm:pl-40 space-y-1.5">
            <div className="h-5 w-40 rounded bg-slate-200 animate-pulse" />
            <div className="h-3.5 w-24 rounded bg-slate-100 animate-pulse" />
          </div>
        </div>
        {/* Product grid skeleton */}
        <div className="px-4 mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
              <div className="aspect-square bg-slate-100 animate-pulse" />
              <div className="p-2.5 space-y-2">
                <div className="h-3 w-full rounded bg-slate-100 animate-pulse" />
                <div className="h-3.5 w-1/3 rounded bg-slate-200 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Not found ─────────────────────────────────────────────────────────────
  if (notFound) return (
    <div className="min-h-screen bg-slate-50">
      <MarketplaceHeader />
      <div className="flex flex-col items-center justify-center py-32 text-center px-4">
        <AlertCircle className="size-12 text-slate-300 mb-4" />
        <h1 className="text-xl font-extrabold text-slate-700 mb-2">Tienda no encontrada</h1>
        <p className="text-sm text-slate-400 mb-6">
          Esta tienda no existe o ya no está disponible en Fast Max.
        </p>
        <Link
          to="/"
          className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white"
          style={{ background: "#B1336E" }}
        >
          Ir al marketplace
        </Link>
      </div>
    </div>
  );

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 font-sans antialiased text-slate-900 flex flex-col">
      <MarketplaceHeader />

      <main className="flex-1">
        {/* Back nav */}
        <div className="mx-auto max-w-5xl px-4 pt-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronLeft className="size-3.5" />
            Volver
          </button>
        </div>

        <div className="mx-auto max-w-5xl pb-10">
          {/* Store banner + profile + name + WA button */}
          <StoreHeader store={store} />

          {/* Products */}
          <StoreProductGrid storeSlug={storeSlug} />
        </div>
      </main>

      <MarketplaceFooter />
      <WhatsAppFloat />
    </div>
  );
}